import clsx from "clsx";
import { formatRelative } from "date-fns";
import { it } from "date-fns/locale";
import { useCallback, useEffect } from "react";
import { NavLink } from "react-router-dom";

import { useLastPreviewUnreadMessagesQuery } from "@hd/api/realtime-message/queries/useLastPreviewUnreadMessagesQuery";
import CompanySelector from "@hd/auth/components/CompanySelector";
import { ContactPanelTabIds } from "@hd/contact-panel/types/contact-panel-tab-ids";
import { useContactPanel } from "@hd/contact-panel/useContactPanel";
import { useHub } from "@hd/hub/useHub";
import { useTranslate } from "@hd/i18n/useTranslate";
import { showOnlinePersonNotification } from "@hd/toast/toasts";
import { SectionIds } from "@hd/types/shared/section-ids";

import { useNotificationsStore } from "./notifications.store";
import { useAuthStore } from "@hd/auth/auth.store";
import { RealtimeUserInfoDto } from "@hd/types/dto/contact/realtime-user-info.dto";
import { UserType } from "@hd/types/dto/shared/enums/user-type";
import { RealtimeChatMessageDto } from "@hd/types/dto/realtime-message/realtime-chat-message.dto";
import { RealtimeMessageAction } from "@hd/types/dto/realtime-message/enums/realtime-message-action";
import { MessageStatusType } from "@hd/types/dto/shared/enums/message-status-type";

function Notifications() {
  // const { items, setRead, setOpen, open, companyId, setCompanyId } =
  //   useNotifications();

  const maxItemsToShow = 5;
  const maxItemsToTake = 30;

  const isChangingGroup = useAuthStore((s) => s.isChangingGroup);
  const items = useNotificationsStore((s) => s.items);
  const init = useNotificationsStore((s) => s.init);
  const push = useNotificationsStore((s) => s.push);
  const companyId = useNotificationsStore((s) => s.companyId);
  const setCompanyId = useNotificationsStore((s) => s.setCompanyId);
  const setRead = useNotificationsStore((s) => s.setRead);
  const { open: openContactPanel } = useContactPanel();

  const t = useTranslate();

  const lastUnreadMessagesQuery = useLastPreviewUnreadMessagesQuery({
    companyId,
    take: maxItemsToTake,
  }, !isChangingGroup);
  const notificationsItems = lastUnreadMessagesQuery.data?.items;
  const unreadMessagesCount =
    lastUnreadMessagesQuery.data?.unreadContactMessagesTotalCount || 0;

  useEffect(() => {
    if (!notificationsItems || !companyId || unreadMessagesCount == 0) {
      init(0, []);
      return;
    }

    init(
      unreadMessagesCount,
      notificationsItems.map((nm) => ({
        type: "message",
        content: nm,
        companyId: companyId,
      }))
    );
  }, [init, notificationsItems, companyId, unreadMessagesCount]);

  useEffect(() => {
    if (items.length < maxItemsToShow && items.length < unreadMessagesCount) {
      lastUnreadMessagesQuery.refetch();
    }
  }, [items, lastUnreadMessagesQuery, unreadMessagesCount]);

  const onMessages = useCallback(
    (messages: RealtimeChatMessageDto[]) => {
      if (companyId) {
        messages.filter( m => m.ConversationId.split(":")[0] === companyId.toString() && m.Sender.Type === UserType.Person)
        .sort( (a, b) => a.ActionTimestamp - b.ActionTimestamp)
        .forEach( message => {
          switch(message.ActionId){
            case RealtimeMessageAction.New:
                push({
                  type: "message",
                  content: {
                    chatMessageId: message.Id,
                    contactId: Number(message.Sender.Id), //Ho filtrato solo i messaggi in arrivo da contacts, quindi il sender id sarà sempre un contactId
                    contactName: message.Sender.Alias,
                    sentDate: new Date(),
                    message: message.Content?.Text || "",
                  },
                  companyId: companyId,
                });
              break;
            case RealtimeMessageAction.Update:
              if(message.Status === MessageStatusType.Read){
                setRead([message.Id]);
              }
              break;
            default:
              break;
          }
        })
      }
    },
    [push, companyId, setRead]
  );

  const onUsers = useCallback(
    (users: RealtimeUserInfoDto[]) => {
      users.filter( u => u.Type === UserType.Person)
      .sort( (a, b) => a.Timestamp - b.Timestamp)
      .forEach( userInfo => {
          if(userInfo.OnlineStatus === true){
            showOnlinePersonNotification("Nuovo utente online");
          }
      });      
    },
    []
  );

  useHub(companyId, { onMessages, onUsers });

  return (
    <div
      tabIndex={0}
      className="select-none align-center shadow-xl bg-white w-80 text-bodyText border-gray-200 border outline-none"
    >
          <div className="px-3 items-center border-b-gray-200 border-b py-2">
              <CompanySelector
                label={t("notifications.chooseCompany")}
                sectionId={SectionIds.ConversationHub}
                selectedCompanyId={companyId}
                selectedCompanyChanged={(companyId) => setCompanyId(companyId)}
              ></CompanySelector>
          </div>
      <ul className="">
        {items.length === 0 ? (
          <div className="py-4 text-center px-3">
            {t("notifications.noNewMessages")}
          </div>
        ) : (
          items
            .filter((item) => item.companyId === companyId)
            .sort(
              (a, b) =>
                b.content.sentDate.getTime() - a.content.sentDate.getTime()
            )
            .slice(0, maxItemsToShow)
            .map((item) => (
              <li
                key={item.content.sentDate.getTime()}
                className={clsx(
                  `flex items-center justify-between border-b border-b-gray-300 w-full overflow-hidden hover:opacity-80 cursor-pointer odd:bg-bodyBg`
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  const contactId = Number(item.content.contactId);
                  const companyId = item.companyId;

                  openContactPanel(
                    contactId,
                    companyId,
                    ContactPanelTabIds.CONVERSATION
                  );
                }}
              >
                <div className={clsx(`px-3 py-2 flex items-center w-full`)}>
                  <div className={` flex-grow overflow-hidden`}>
                    <div className="text-xs font-bold text-accent">
                      {item.content.contactName}
                    </div>
                    <div className="text-xs  text-ellipsis text-nowrap overflow-hidden">
                      {item.content.message}
                    </div>
                    <div className="text-xs font-bold">
                      {formatRelative(item.content.sentDate, new Date(), {
                        locale: it,
                      })}
                    </div>
                  </div>
                </div>
              </li>
            ))
        )}
      </ul>

      <div className="py-2 text-center px-3 bg-bodyBg">
        <NavLink
          className="underline text-xs"
          to={"/conversation-hub?companyId=" + companyId}
        >
          Vai all'hub conversazioni
        </NavLink>
      </div>
    </div>
  );
}

export default Notifications;
